'use client';

import { useState } from 'react';

import type { MobileAppDebugLogEntry } from '@/shared-mobile-app';
import { Button } from '@/shared-ui';

interface Props {
  entry: MobileAppDebugLogEntry;
}

export default function ProjectUsabilityMobileAppDebugLogRow({ entry }: Props) {
  const [expanded, setExpanded] = useState(false);
  const time = new Date(entry.timestamp).toLocaleTimeString();
  const detailString =
    entry.detail !== undefined ? safeStringify(entry.detail) : null;

  return (
    <li
      data-level={entry.level}
      data-source={entry.source}
      className='border-b border-white/5 py-0.5 last:border-0 data-[level=error]:text-red-300 data-[level=warn]:text-amber-300 data-[source=mic]:text-cyan-200 data-[source=recorder]:text-fuchsia-200 data-[source=session]:text-emerald-200'
    >
      <Button
        variant='ghost'
        data-test='mobile-app-debug-log-toggle'
        disabled={!detailString}
        onClick={() => setExpanded((v) => !v)}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} log entry: ${entry.label}`}
        aria-expanded={detailString ? expanded : undefined}
        className='flex h-auto w-full items-start justify-between gap-2 rounded-none p-0 text-left focus-visible:outline-1 focus-visible:outline-white/40 hover:enabled:bg-transparent disabled:cursor-default'
      >
        <span>
          <span className='text-white/40'>{time}</span>{' '}
          <span className='text-white/60'>[{entry.source}]</span>{' '}
          <span>{entry.label}</span>
        </span>
        {detailString ? (
          <span aria-hidden='true' className='text-white/40'>
            {expanded ? '−' : '+'}
          </span>
        ) : null}
      </Button>
      {expanded && detailString ? (
        <pre className='mt-0.5 max-h-24 overflow-auto rounded bg-black/40 p-1 text-[9px] whitespace-pre-wrap text-white/70'>
          {detailString}
        </pre>
      ) : null}
    </li>
  );
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, replacer, 2);
  } catch {
    return String(value);
  }
}

function replacer(_key: string, value: unknown): unknown {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (value instanceof ArrayBuffer) {
    return `[binary ${value.byteLength} bytes]`;
  }
  if (ArrayBuffer.isView(value)) {
    return `[binary ${value.byteLength} bytes]`;
  }
  return value;
}
